/**
 * UTF-8 for the pure package (ADR-067 decision 1): the Name component carries each part of a name as UTF-8
 * bytes, and `odyssey-shared` runs without `TextEncoder` or `TextDecoder` (its `lib` is ES2022 alone), so it
 * encodes and decodes the standard form itself. Decoding is strict: a stray continuation byte, a sequence cut
 * short, an overlong form, a surrogate code point or one past U+10FFFF is malformed and answered with null,
 * never replaced or guessed at, so a frame carrying one is refused whole.
 */

/**
 * The largest code point Unicode defines.
 */
const MAX_CODE_POINT = 0x10ffff;

/**
 * Whether a UTF-16 code unit opens a surrogate pair.
 *
 * @param unit The code unit.
 * @returns True for a high surrogate.
 */
function isHighSurrogate(unit: number): boolean {
  return unit >= 0xd800 && unit <= 0xdbff;
}

/**
 * Whether a UTF-16 code unit closes a surrogate pair.
 *
 * @param unit The code unit, or NaN past the end of a string.
 * @returns True for a low surrogate.
 */
function isLowSurrogate(unit: number): boolean {
  return unit >= 0xdc00 && unit <= 0xdfff;
}

/**
 * The number of bytes a string takes in UTF-8.
 *
 * @param text The text.
 * @returns The byte count, or null when the text holds a lone surrogate, which has no UTF-8 form.
 * @example
 * utf8Length("Zoë");
 * // 4
 */
export function utf8Length(text: string): number | null {
  let bytes = 0;
  for (let i = 0; i < text.length; i += 1) {
    const unit = text.charCodeAt(i);
    if (unit < 0x80) {
      bytes += 1;
    } else if (unit < 0x800) {
      bytes += 2;
    } else if (isHighSurrogate(unit)) {
      if (!isLowSurrogate(text.charCodeAt(i + 1))) {
        return null;
      }
      bytes += 4;
      i += 1;
    } else if (isLowSurrogate(unit)) {
      return null;
    } else {
      bytes += 3;
    }
  }
  return bytes;
}

/**
 * Encodes a string as UTF-8.
 *
 * @param text The text.
 * @returns Its bytes, in a new array.
 * @throws RangeError when the text holds a lone surrogate.
 */
export function encodeUtf8(text: string): Uint8Array {
  const length = utf8Length(text);
  if (length === null) {
    throw new RangeError("the text holds a lone surrogate, which UTF-8 cannot carry");
  }
  const out = new Uint8Array(length);
  let at = 0;
  for (let i = 0; i < text.length; i += 1) {
    let point = text.charCodeAt(i);
    if (isHighSurrogate(point)) {
      point = 0x10000 + ((point - 0xd800) << 10) + (text.charCodeAt(i + 1) - 0xdc00);
      i += 1;
    }
    if (point < 0x80) {
      out[at] = point;
      at += 1;
    } else if (point < 0x800) {
      out[at] = 0xc0 | (point >> 6);
      out[at + 1] = 0x80 | (point & 0x3f);
      at += 2;
    } else if (point < 0x10000) {
      out[at] = 0xe0 | (point >> 12);
      out[at + 1] = 0x80 | ((point >> 6) & 0x3f);
      out[at + 2] = 0x80 | (point & 0x3f);
      at += 3;
    } else {
      out[at] = 0xf0 | (point >> 18);
      out[at + 1] = 0x80 | ((point >> 12) & 0x3f);
      out[at + 2] = 0x80 | ((point >> 6) & 0x3f);
      out[at + 3] = 0x80 | (point & 0x3f);
      at += 4;
    }
  }
  return out;
}

/**
 * Decodes the UTF-8 in a slice of a buffer.
 *
 * @param bytes The buffer.
 * @param start The slice's first byte.
 * @param end One past the slice's last byte.
 * @returns The text, or null when the slice is out of the buffer's range or its bytes are not well-formed
 *   UTF-8, a sequence the slice's end cuts short included.
 */
export function decodeUtf8(bytes: Uint8Array, start: number, end: number): string | null {
  if (start < 0 || start > end || end > bytes.length) {
    return null;
  }
  let text = "";
  let i = start;
  while (i < end) {
    const lead = bytes[i] as number;
    let point: number;
    let follow: number;
    let least: number;
    if (lead < 0x80) {
      point = lead;
      follow = 0;
      least = 0;
    } else if (lead >= 0xc2 && lead <= 0xdf) {
      point = lead & 0x1f;
      follow = 1;
      least = 0x80;
    } else if (lead >= 0xe0 && lead <= 0xef) {
      point = lead & 0x0f;
      follow = 2;
      least = 0x800;
    } else if (lead >= 0xf0 && lead <= 0xf4) {
      point = lead & 0x07;
      follow = 3;
      least = 0x10000;
    } else {
      return null;
    }
    if (i + follow >= end && follow > 0) {
      return null;
    }
    for (let k = 1; k <= follow; k += 1) {
      const next = bytes[i + k] as number;
      if ((next & 0xc0) !== 0x80) {
        return null;
      }
      point = (point << 6) | (next & 0x3f);
    }
    if (point < least || point > MAX_CODE_POINT || (point >= 0xd800 && point <= 0xdfff)) {
      return null;
    }
    text += String.fromCodePoint(point);
    i += 1 + follow;
  }
  return text;
}
